import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/pages/About";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import ProfileForm from "@/components/ProfileForm";
import { appApi, contentApi } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Layers, ListChecks } from "lucide-react";

const money = (n) => `$${n.toLocaleString()} USD`;

const ApplyStart = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [pricing, setPricing] = useState({ full: { 1: 4500, 2: 10000 }, per_category: { 1: 900, 2: 2000 } });
  const [scopeType, setScopeType] = useState("full");
  const [duration, setDuration] = useState(1);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    contentApi.framework().then(({ data }) => {
      setCategories(data.categories || []);
      if (data.pricing) setPricing(data.pricing);
    });
  }, []);

  const price = scopeType === "full"
    ? pricing.full[duration]
    : pricing.per_category[duration] * Math.max(0, selected.length);

  const toggleCat = (key) => setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));

  const handleCreate = async (data) => {
    if (scopeType === "category" && selected.length === 0) {
      toast.error("Select at least one assessment category");
      return;
    }
    try {
      const payload = {
        ...data,
        scope_type: scopeType,
        selected_categories: scopeType === "full" ? categories.map((c) => c.key) : selected,
        duration_years: duration,
      };
      const { data: app } = await appApi.create(payload);
      toast.success("Application created");
      navigate(`/applications/${app.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create application");
    }
  };

  const ScopeCard = ({ type, icon: Icon, title, desc }) => (
    <button type="button" onClick={() => setScopeType(type)} data-testid={`scope-${type}`}
      className={`flex w-full items-start gap-3 rounded-xl border p-4 text-left transition-colors ${scopeType === type ? "border-wgreen-600 bg-wgreen-50" : "border-[var(--wechr-border)] hover:border-wgreen-400"}`}>
      <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${scopeType === type ? "bg-wgreen-600 text-white" : "bg-[var(--wechr-subtle)] text-navy-700"}`}><Icon className="h-5 w-5" /></div>
      <div className="flex-1">
        <div className="flex items-center gap-2"><p className="font-heading text-sm font-semibold text-navy-900">{title}</p>{scopeType === type && <CheckCircle2 className="h-4 w-4 text-wgreen-600" />}</div>
        <p className="mt-0.5 text-xs text-[var(--wechr-muted-text)]">{desc}</p>
      </div>
    </button>
  );

  return (
    <div>
      <PageHeader eyebrow="Apply" title="Start your certification application"
        subtitle="Choose your certification scope and validity, tell us about your organization, then complete the questionnaire and upload evidence. A WECH-RB reviewer verifies your evidence before certification." />
      <section className="wechr-section">
        <div className="wechr-container max-w-3xl space-y-6">
          {/* Scope */}
          <Card>
            <CardContent className="p-6 sm:p-8">
              <h2 className="font-heading text-lg font-semibold text-navy-900">1. Choose your certification scope</h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <ScopeCard type="full" icon={Layers} title="Full WECH-RB Certification" desc="All six assessment categories." />
                <ScopeCard type="category" icon={ListChecks} title="Category-Based Certification" desc="Certify only the categories you select." />
              </div>

              {scopeType === "category" && (
                <div className="mt-5" data-testid="category-select">
                  <p className="mb-2 text-sm font-medium text-navy-800">Select categories to certify</p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {categories.map((c) => (
                      <label key={c.key} className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 ${selected.includes(c.key) ? "border-wgreen-600 bg-wgreen-50" : "border-[var(--wechr-border)]"}`} data-testid={`category-option-${c.key}`}>
                        <Checkbox checked={selected.includes(c.key)} onCheckedChange={() => toggleCat(c.key)} />
                        <span className="text-sm text-navy-800">{c.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Duration */}
              <p className="mb-2 mt-6 text-sm font-medium text-navy-800">Certificate validity</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {[1, 2].map((d) => (
                  <button key={d} type="button" onClick={() => setDuration(d)} data-testid={`duration-${d}`}
                    className={`rounded-xl border p-4 text-left transition-colors ${duration === d ? "border-wgreen-600 bg-wgreen-50" : "border-[var(--wechr-border)] hover:border-wgreen-400"}`}>
                    <p className="font-heading text-sm font-semibold text-navy-900">{d} Year{d > 1 ? "s" : ""}</p>
                    <p className="mt-0.5 text-xs text-[var(--wechr-muted-text)]">
                      {scopeType === "full" ? money(pricing.full[d]) : `${money(pricing.per_category[d])} per category`}
                    </p>
                  </button>
                ))}
              </div>

              {/* Price summary */}
              <div className="mt-6 flex items-center justify-between rounded-xl bg-navy-900 px-5 py-4 text-white" data-testid="price-summary">
                <div>
                  <p className="text-xs uppercase tracking-wide text-white/60">Estimated total</p>
                  <p className="text-xs text-white/80">{scopeType === "full" ? "All 6 categories" : `${selected.length} categor${selected.length === 1 ? "y" : "ies"}`} · {duration} year{duration > 1 ? "s" : ""}</p>
                </div>
                <p className="font-heading text-2xl font-bold" data-testid="price-amount">{money(price || 0)}</p>
              </div>
            </CardContent>
          </Card>

          {/* Profile */}
          <Card>
            <CardContent className="p-6 sm:p-8">
              <h2 className="mb-4 font-heading text-lg font-semibold text-navy-900">2. About your organization</h2>
              <ProfileForm
                initial={{ company_name: user?.company_name || "", contact_person: user?.name || "", contact_email: user?.email || "" }}
                onSubmit={handleCreate}
                submitLabel="Create application"
              />
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default ApplyStart;
